import {
    ConfigurableItemMapper,
    DataProviderConfigurations,
    InMemoryCrosspointDataService,
    Viewport
} from "@o2d/datasource";
import { DateTime, Interval } from "luxon";
import { axisEntityMetadata } from "../../services/backend-axis-data.service";
import { ResourcesCrosspointDataService } from "./connectivity/resources-crosspoint-data.service";
import { dateTimeAxisBuilder, entityAxisBuilder } from "../../connectivity/viewport-query.types";
import type { HeaderItem, DateTimeHeader } from "../_shared/types/header-item.type";
import type { ResourcePlanningItem } from "./types/resource-planning-item.type";
import { createColorFilterControlDataProvider } from "../_shared/timeline-data-providers.factory";

/**
 * Creates data provider configurations for the resource planning view.
 * Uses ResourcesCrosspointDataService (custom endpoint) for content,
 * and shared InMemory providers for headers.
 *
 * `grid-area-control1-static` (A×C3) is the same shared, self-contained
 * provider entry the standard timeline views use — see
 * createColorFilterControlDataProvider's own docstring — reused verbatim
 * rather than duplicated, matching createColorFilterControlAreaConfiguration
 * in resource-planning-area.factory.ts.
 */
export const createResourcePlanningDataProviders = (): DataProviderConfigurations<unknown> => [
    createColorFilterControlDataProvider(),
    // Header-DataProvider: Employee row labels from axis metadata
    {
        axisIdX: 'A',
        axisIdY: 'D',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, _xAxis: any, yAxis: any): HeaderItem[] => {
                const items: HeaderItem[] = [];
                const [minY, maxY] = yAxis.loadedBoundaries;

                for (let yi = minY; yi <= maxY; yi++) {
                    const yValue = yAxis.getValue(yi);
                    if (!yValue) continue;

                    const meta = axisEntityMetadata.get(yValue);
                    items.push({
                        uid: yValue,
                        title: meta?.title ?? yValue,
                        color: meta?.color ?? null,
                        textColor: meta?.textColor ?? null,
                        entities: [{ uid: yValue }],
                        interval: Interval.fromDateTimes(DateTime.now(), DateTime.now())
                    });
                }
                return items;
            }
        },
        itemMapper: new ConfigurableItemMapper<number, string, HeaderItem>({
            x: () => [0, 2],
            y: 'entities[*].uid',
            idPath: 'uid'
        })
    },
    // Content-DataProvider: Issues from resources-viewport-query endpoint
    {
        axisIdX: 'B',
        axisIdY: 'D',
        itemDataService: {
            class: ResourcesCrosspointDataService,
            xAxisConfig: dateTimeAxisBuilder,
            yAxisConfig: entityAxisBuilder({ entityType: 'Employee', sort: { strategy: 'property', path: 'lastName', direction: 'ASC' } })
        },
        itemMapper: new ConfigurableItemMapper<string, string, ResourcePlanningItem>({
            x: (item: ResourcePlanningItem) => [
                item.interval.start!.toISODate()!,
                item.interval.end!.minus({ days: 1 }).toISODate()!
            ],
            y: 'employees[*].uid',
            idPath: 'uid'
        })
    },
    // DateTime Header: Month/Year
    {
        axisIdX: 'B',
        axisIdY: 'C0',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: any, _yAxis: any): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                const startDate = DateTime.fromISO(startX).startOf('day');
                const endDate = DateTime.fromISO(endX).startOf('day');

                return Interval
                    .fromDateTimes(startDate.startOf('month'), endDate.plus({ months: 1 }).startOf('month'))
                    .splitBy({ months: 1 })
                    .map(month => {
                        const rangeStart = DateTime.max(month.start!, startDate);
                        const rangeEnd = DateTime.min(month.end!.minus({ day: 1 }), endDate);
                        return {
                            range: [rangeStart.toISODate()!, rangeEnd.toISODate()!] as [string, string],
                            id: `xheader_${month.start!.toFormat('yyyy-LL')}`
                        };
                    });
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: { start: 'range.0', end: 'range.1' },
            y: () => [0, 0],
            idPath: 'id'
        })
    },
    // DateTime Header: Calendar Week
    {
        axisIdX: 'B',
        axisIdY: 'C1',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: any, _yAxis: any): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                let startDateX = DateTime.fromISO(startX).startOf('day');
                const endDateX = DateTime.fromISO(endX).startOf('day');
                const weeks: DateTimeHeader[] = [];

                if (startDateX.localWeekday !== 1) {
                    const nextBeginOfWeek = startDateX.plus({ days: (8 - startDateX.localWeekday) });
                    weeks.push({
                        range: [startDateX.toISODate()!, nextBeginOfWeek.minus({ days: 1 }).toISODate()!],
                        id: `xheader_${startDateX.year}-${startDateX.localWeekNumber}`
                    });
                    startDateX = nextBeginOfWeek;
                }

                const fullWeeks = Interval
                    .fromDateTimes(startDateX, endDateX)
                    .splitBy({ week: 1 })
                    .map<DateTimeHeader>(week => ({
                        range: [week.start!.toISODate()!, week.end!.minus({ days: 1 }).toISODate()!],
                        id: `xheader_${week.start!.year}-${week.start!.localWeekNumber}`
                    }));

                return [...weeks, ...fullWeeks];
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: { start: 'range.0', end: 'range.1' },
            y: () => [0, 0],
            idPath: 'id'
        })
    },
    // DateTime Header: Weekday
    {
        axisIdX: 'B',
        axisIdY: 'C2',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: any, _yAxis: any): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                return Interval
                    .fromDateTimes(DateTime.fromISO(startX).startOf('day'), DateTime.fromISO(endX).startOf('day'))
                    .splitBy({ day: 1 })
                    .map(day => ({
                        range: [day.start!.toISODate()!, day.start!.toISODate()!] as [string, string],
                        id: `xheader_weekday_${day.start!.toISODate()}`
                    }));
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: { start: 'range.0', end: 'range.1' },
            y: () => [0, 0],
            idPath: 'id'
        })
    },
    // DateTime Header: Day of Month
    {
        axisIdX: 'B',
        axisIdY: 'C3',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: any, _yAxis: any): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                return Interval
                    .fromDateTimes(DateTime.fromISO(startX).startOf('day'), DateTime.fromISO(endX).startOf('day'))
                    .splitBy({ day: 1 })
                    .map(day => ({
                        range: [day.start!.toISODate()!, day.start!.toISODate()!] as [string, string],
                        id: `xheader_day_${day.start!.toISODate()}`
                    }));
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: { start: 'range.0', end: 'range.1' },
            y: () => [0, 0],
            idPath: 'id'
        })
    }
];
